import React, { useState, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import html2canvas from 'html2canvas';
import Config from '../../_config/config';
import { getChartPalette, useThemeVersion } from '../../theme/chartTheme';

interface ChartBuilderModalProps {
  show: boolean;
  onHide: () => void;
  onInsert: (imgUrl: string) => void;
}

type ChartType = 'bar' | 'line' | 'pie' | 'scatter';

interface DataRow {
  label: string;
  value: number;
  value2?: number;
}

const ChartBuilderModal: React.FC<ChartBuilderModalProps> = ({ show, onHide, onInsert }) => {
  // Theme-aware palette — resolved so html2canvas snapshots the actual colors.
  const themeVersion = useThemeVersion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const COLORS = React.useMemo(() => getChartPalette(8), [themeVersion]);
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [title, setTitle] = useState('');
  const [xLabel, setXLabel] = useState('');
  const [yLabel, setYLabel] = useState('');
  const [rows, setRows] = useState<DataRow[]>([
    { label: 'Item 1', value: 10 },
    { label: 'Item 2', value: 25 },
    { label: 'Item 3', value: 15 },
  ]);
  const [uploading, setUploading] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const addRow = () => setRows(prev => [...prev, { label: `Item ${prev.length + 1}`, value: 0 }]);
  const removeRow = (i: number) => setRows(prev => prev.filter((_, idx) => idx !== i));
  const updateRow = (i: number, field: keyof DataRow, val: string) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: field === 'label' ? val : Number(val) || 0 } : r));
  };

  const handleInsert = async () => {
    if (!chartRef.current) return;
    setUploading(true);
    try {
      // Capture chart as image
      const canvas = await html2canvas(chartRef.current, { backgroundColor: 'var(--surface-card)', scale: 2 });
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((b) => resolve(b!), 'image/png');
      });

      // Upload to S3 via existing endpoint
      const formData = new FormData();
      formData.append('upload', blob, 'chart.png');
      const token = (() => {
        try {
          const cu = localStorage.getItem('currentUser');
          if (cu) return JSON.parse(cu).token;
        } catch { /* ignore */ }
        return '';
      })();
      const resp = await fetch(`${Config.nodeSiteUrl}v1/question-bank/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await resp.json();
      if (result.url) {
        onInsert(result.url);
        handleClose();
      }
    } catch (err) {
      console.error('Chart upload failed:', err);
    }
    setUploading(false);
  };

  const handleClose = () => {
    setTitle('');
    setXLabel('');
    setYLabel('');
    setChartType('bar');
    setRows([
      { label: 'Item 1', value: 10 },
      { label: 'Item 2', value: 25 },
      { label: 'Item 3', value: 15 },
    ]);
    onHide();
  };

  const renderChart = () => {
    const data = rows.map(r => ({ name: r.label, value: r.value }));

    switch (chartType) {
      case 'bar':
        return (
          <BarChart width={420} height={280} data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined} />
            <YAxis label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined} />
            <Tooltip />
            <Bar dataKey="value" fill="var(--brand-primary)">
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        );
      case 'line':
        return (
          <LineChart width={420} height={280} data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined} />
            <YAxis label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined} />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="var(--brand-primary)" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        );
      case 'pie':
        return (
          <PieChart width={420} height={280}>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        );
      case 'scatter':
        return (
          <ScatterChart width={420} height={280}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" name="Label" label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined} />
            <YAxis dataKey="value" name="Value" label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={data} fill="var(--brand-primary)">
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Scatter>
          </ScatterChart>
        );
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton style={{ background: 'var(--gradient-brand)', color: 'var(--text-on-brand)' }}>
        <Modal.Title style={{ fontSize: 16 }}>Insert Chart</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 20 }}>
        <div style={{ display: 'flex', gap: 20 }}>
          {/* Left: Configuration */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Chart Type</Form.Label>
                <Form.Select size="sm" value={chartType} onChange={e => setChartType(e.target.value as ChartType)}>
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="pie">Pie Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </Form.Select>
              </Form.Group>
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Title</Form.Label>
                <Form.Control size="sm" value={title} onChange={e => setTitle(e.target.value)} placeholder="Chart title" />
              </Form.Group>
            </div>
            {chartType !== 'pie' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <Form.Group style={{ flex: 1 }}>
                  <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>X-Axis Label</Form.Label>
                  <Form.Control size="sm" value={xLabel} onChange={e => setXLabel(e.target.value)} />
                </Form.Group>
                <Form.Group style={{ flex: 1 }}>
                  <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Y-Axis Label</Form.Label>
                  <Form.Control size="sm" value={yLabel} onChange={e => setYLabel(e.target.value)} />
                </Form.Group>
              </div>
            )}

            {/* Data rows */}
            <label style={{ fontSize: 12, fontWeight: 600 }}>Data</label>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6, padding: 6, marginBottom: 8 }}>
              {rows.map((row, i) => (
                <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
                  <Form.Control
                    size="sm" style={{ flex: 2 }}
                    value={row.label}
                    onChange={e => updateRow(i, 'label', e.target.value)}
                    placeholder="Label"
                  />
                  <Form.Control
                    size="sm" type="number" style={{ flex: 1 }}
                    value={row.value}
                    onChange={e => updateRow(i, 'value', e.target.value)}
                    placeholder="Value"
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    style={{ border: 'none', background: 'none', color: 'var(--status-danger)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}
                    disabled={rows.length <= 1}
                  >&times;</button>
                </div>
              ))}
            </div>
            <Button variant="secondary" size="sm" onClick={addRow}>+ Add Row</Button>
          </div>

          {/* Right: Preview */}
          <div style={{ flex: '0 0 auto' }}>
            <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' }}>Preview</label>
            <div
              ref={chartRef}
              style={{
                width: 440, padding: 10, background: 'var(--surface-card)',
                border: '1px solid var(--border-subtle)', borderRadius: 8,
              }}
            >
              {title && <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{title}</div>}
              {renderChart()}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleInsert} disabled={uploading || rows.length === 0}>
          {uploading ? 'Uploading...' : 'Insert Chart'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChartBuilderModal;
